import { useState, useRef, useEffect } from "react";
import { 
  useGetWorkbenchItems, 
  useGetWorkbenchFavourites, 
  useAddWorkbenchItem, 
  useRemoveWorkbenchItem, 
  useGenerateWorkbenchSummary,
} from "../useAiWorkbench";
import { useSseAnalysis } from "./useSseAnalysis";
import { UploadedDocument, validateAndReadFiles } from "./types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Wand2, FileText, Upload, Calendar, X, Bot, Loader2, Sparkles, ShieldAlert, ExternalLink } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { useToast } from "@/hooks/use-toast";

export function WorkbenchTab() {
  const { data: workbenchData, isLoading: workbenchLoading } = useGetWorkbenchItems();
  const { data: favData, isLoading: favLoading } = useGetWorkbenchFavourites();
  const addItem = useAddWorkbenchItem();
  const removeItem = useRemoveWorkbenchItem();
  const generateSummary = useGenerateWorkbenchSummary();
  const { toast } = useToast();

  const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
  const [removeConfirmId, setRemoveConfirmId] = useState<number | null>(null);

  // Transient upload for blank tender analysis
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadRequiredMessage, setUploadRequiredMessage] = useState("");
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [pdfBase64s, setPdfBase64s] = useState<UploadedDocument[]>([]);
  
  const { result: sseResult, loading: sseLoading, runAnalysis, abort, reset: resetAnalysis } = useSseAnalysis();
  const sseResultRef = useRef<HTMLDivElement>(null);

  const workbenchItems = workbenchData?.items ?? [];
  const selectedItem = workbenchItems.find(i => i.id === selectedItemId) || null;
  const favourites = favData?.favourites ?? [];
  const workbenchTenderIds = favData?.workbenchTenderIds ?? [];
  const eligibleFavourites = favourites.filter(f => !workbenchTenderIds.includes(f.tenderId));

  useEffect(() => {
    if (sseResult && sseResultRef.current) {
      sseResultRef.current.scrollTop = sseResultRef.current.scrollHeight;
    }
  }, [sseResult]);

  async function handleAdd(tenderId: number) {
    try {
      await addItem.mutateAsync(tenderId);
      toast({ title: "已加入工作台" });
    } catch (e: any) {
      toast({ title: "無法加入", description: e.message, variant: "destructive" });
    }
  }

  async function handleRemove() {
    if (!removeConfirmId) return;
    try {
      await removeItem.mutateAsync(removeConfirmId);
      toast({ title: "已從工作台移除" });
      if (selectedItemId === removeConfirmId) setSelectedItemId(null);
    } catch (e: any) {
      toast({ title: "移除失敗", description: e.message, variant: "destructive" });
    } finally {
      setRemoveConfirmId(null);
    }
  }

  async function handleGenerateSummary() {
    if (!selectedItem) return;
    try {
      const isRegenerate = !!selectedItem.summary?.isStale;
      const { reused } = await generateSummary.mutateAsync(selectedItem.id);
      toast({ 
        title: isRegenerate ? "已重新生成摘要" : "已生成摘要", 
        description: reused ? "提取了現有的共用摘要。" : "已生成並儲存新摘要。" 
      });
    } catch (e: any) {
      if (e.code === "UPLOAD_REQUIRED") {
        setUploadRequiredMessage(e.message);
        setShowUploadDialog(true);
        toast({ title: "需要完整標書", description: e.message });
        return;
      }
      toast({ title: "生成失敗", description: e.message, variant: "destructive" });
    }
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) {
      const newFiles = Array.from(e.target.files);
      const result = await validateAndReadFiles(newFiles, pdfFiles);
      
      if (result.error) {
        toast({ title: "檔案錯誤", description: result.error, variant: "destructive" });
      }
      
      if (result.validFiles.length > 0) {
        setPdfFiles(prev => [...prev, ...result.validFiles]);
        setPdfBase64s(prev => [...prev, ...result.base64s]);
      }
    }
    e.target.value = "";
  }

  function startTransientAnalysis() {
    if (!selectedItem) return;
    runAnalysis({
      mode: 1,
      documents: pdfBase64s,
      tenderId: selectedItem.tender.id
    });
  }

  function closeUploadDialog() {
    abort();
    resetAnalysis();
    setPdfFiles([]);
    setPdfBase64s([]);
    setUploadRequiredMessage("");
    setShowUploadDialog(false);
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:h-[calc(100vh-12rem)] md:min-h-[600px]">
      {/* Left Pane - Favourites to add */}
      <div className="md:col-span-1 border rounded-lg bg-card flex flex-col overflow-hidden max-h-[40vh] md:max-h-none">
        <div className="p-4 border-b bg-muted/40">
          <h2 className="font-semibold text-sm">已收藏招標（可加入）</h2>
          <p className="text-xs text-muted-foreground">加入工作台以開始投標流程</p>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {favLoading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-md" />)
          ) : eligibleFavourites.length === 0 ? (
            <div className="text-center p-6 text-muted-foreground text-sm">
              沒有可加入的已收藏招標。
            </div>
          ) : (
            eligibleFavourites.map(f => (
              <div key={f.tenderId} className="p-3 border rounded-md hover:bg-muted/50 transition-colors flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium leading-snug line-clamp-2">{f.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {f.deadlineDate || "無截止日期"}
                  </p>
                  <p className={`text-xs mt-1 flex items-center gap-1 ${f.documentCount > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}`}>
                    <FileText className="w-3 h-3" />
                    {f.documentCount > 0 ? `有文件（${f.documentCount}）` : "沒有文件"}
                  </p>
                </div>
                <Button size="icon" variant="ghost" className="shrink-0 h-8 w-8 text-primary hover:bg-primary/10" onClick={() => handleAdd(f.tenderId)} disabled={addItem.isPending}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Pane - Workbench Items */}
      <div className="md:col-span-2 border rounded-lg bg-card flex flex-col overflow-hidden">
        <div className="p-4 border-b bg-muted/20 flex gap-2 overflow-x-auto shrink-0 hide-scrollbar">
          {workbenchLoading ? (
             <Skeleton className="h-9 w-32 rounded-full" />
          ) : workbenchItems.length === 0 ? (
            <div className="text-sm text-muted-foreground py-1">工作台目前為空。</div>
          ) : (
            workbenchItems.map(item => (
              <button
                key={item.id}
                onClick={() => setSelectedItemId(item.id)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
                  selectedItemId === item.id 
                    ? "bg-primary text-primary-foreground border-primary" 
                    : "bg-background hover:bg-muted border-border"
                }`}
              >
                {item.tender.district ? `${item.tender.district} - ` : ""}{item.tender.title.substring(0, 15)}...
              </button>
            ))
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-0">
          {!selectedItem ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground opacity-50 p-8 text-center">
              <Bot className="h-12 w-12 mb-4" />
              <p className="font-medium">請選擇工作台項目</p>
              <p className="text-sm mt-1">生成 AI 摘要並管理投標流程。</p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold leading-snug">{selectedItem.tender.title}</h2>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <Badge variant={selectedItem.tender.status === "open" ? "default" : "secondary"}>
                      {selectedItem.tender.status === "open" ? "招標中" : "已截止"}
                    </Badge>
                    {selectedItem.tender.deadlineDate && (
                      <span className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" /> 截止日期：{selectedItem.tender.deadlineDate}
                      </span>
                    )}
                    <Badge variant="outline" className={selectedItem.tender.documentUrls.length > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-400" : ""}>
                      <FileText className="w-3 h-3 mr-1" />
                      {selectedItem.tender.documentUrls.length > 0
                        ? `有文件連結（${selectedItem.tender.documentUrls.length}）`
                        : "沒有文件連結"}
                    </Badge>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="shrink-0 text-destructive hover:bg-destructive/10" onClick={() => setRemoveConfirmId(selectedItem.id)}>
                  <Trash2 className="w-4 h-4 mr-1.5" /> 移除
                </Button>
              </div>

              {selectedItem.tender.documentUrls.length === 0 && (
                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg p-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-amber-800 dark:text-amber-400 text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" /> 未提供招標文件
                    </h3>
                    <p className="text-xs text-amber-700/80 dark:text-amber-500/80 mt-1">
                      此招標沒有緩存文件。你可以手動上載空白標書以進行一次性分析。
                    </p>
                  </div>
                  <Button size="sm" variant="outline" className="shrink-0 bg-white dark:bg-black" onClick={() => setShowUploadDialog(true)}>
                    <Upload className="w-4 h-4 mr-1.5" /> 上載並分析
                  </Button>
                </div>
              )}

              {selectedItem.tender.documentUrls.length > 0 && (
                <div className="border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/20 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-800 dark:text-emerald-300 text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    已找到 {selectedItem.tender.documentUrls.length} 個文件連結
                  </h3>
                  <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80 mt-1">
                    按「生成／提取摘要」後，系統會直接下載公開文件並交給 AI助手分析。
                  </p>
                  <div className="mt-3 flex flex-col gap-1.5">
                    {selectedItem.tender.documentUrls.map((url, index) => (
                      <a
                        key={url}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1.5 w-fit break-all"
                      >
                        文件 {index + 1}
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted/30 p-3 border-b flex flex-wrap justify-between items-center gap-2">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    共用 AI 摘要
                    {selectedItem.summary?.isStale && (
                      <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950 dark:border-amber-800">已過期</Badge>
                    )}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowUploadDialog(true)}
                      className="h-8 text-xs"
                    >
                      <Upload className="w-3.5 h-3.5 mr-1.5" />
                      手動上載分析
                    </Button>
                    {(!selectedItem.summary || selectedItem.summary.isStale) && selectedItem.tender.documentUrls.length > 0 && (
                      <Button
                        size="sm"
                        onClick={handleGenerateSummary}
                        disabled={generateSummary.isPending}
                        className="h-8 text-xs"
                      >
                        {generateSummary.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
                        {selectedItem.summary?.isStale ? "重新生成摘要" : "生成 / 提取摘要"}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="p-5 bg-card">
                  {selectedItem.summary ? (
                    <div className="space-y-4">
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-a:text-primary">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedItem.summary.content}</ReactMarkdown>
                      </div>
                      <div className="pt-4 border-t text-xs text-muted-foreground flex justify-between">
                        <span>模型：{selectedItem.summary.modelId}</span>
                        <span>最後更新：{new Date(selectedItem.summary.updatedAt).toLocaleString("zh-HK")}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      {selectedItem.tender.documentUrls.length > 0 
                        ? "尚未生成摘要。請點擊上方按鈕生成。"
                        : "缺乏招標文件，無法生成共用摘要。"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={removeConfirmId !== null}
        title="從工作台移除"
        description="確定要將此招標從工作台移除嗎？該項目仍會保留在「我的最愛」中。"
        confirmLabel="移除"
        onConfirm={handleRemove}
        onCancel={() => setRemoveConfirmId(null)}
      />

      <Dialog open={showUploadDialog} onOpenChange={(open) => !open && closeUploadDialog()}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>上載完整標書作分析</DialogTitle>
            <DialogDescription>
              上載供應商需要填寫或提交的完整招標文件，以進行一次性分析。此結果不會儲存到共用摘要中。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {uploadRequiredMessage && (
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-lg p-3">
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">現有連結的文件不完整</p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{uploadRequiredMessage}</p>
              </div>
            )}
            <div className="bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-3 space-y-1.5">
              <div className="flex items-center gap-2 text-blue-800 dark:text-blue-300 font-semibold text-sm">
                <ShieldAlert className="w-4 h-4" />
                私隱與安全聲明
              </div>
              <p className="text-xs text-blue-700/90 dark:text-blue-400/90 leading-relaxed">
                Skoltend 不會儲存上載的檔案或其敏感內容。文件僅作暫存，並直接傳送至所選的 AI 供應商進行分析。
              </p>
              <p className="text-[10px] text-blue-600/70 dark:text-blue-500/70 leading-relaxed">
                免責聲明：AI 僅作輔助用途，不能保證內容完全正確或確保中標。請務必人工核對所有提交內容。
              </p>
            </div>

            <div className="space-y-2">
              {pdfFiles.length > 0 && (
                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                  {pdfFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm bg-muted/50 p-2 rounded border">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="flex-1 truncate">{file.name}</span>
                      <button onClick={() => {
                        setPdfFiles(prev => prev.filter((_, i) => i !== idx));
                        setPdfBase64s(prev => prev.filter((_, i) => i !== idx));
                      }} className="text-muted-foreground hover:text-destructive">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                <Upload className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">點擊選擇檔案（最多 10 個，總計最大 50MB）</span>
                <input type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.txt" onChange={handleFileChange} />
              </label>
            </div>

            {sseResult || sseLoading ? (
              <div className="border rounded-lg flex flex-col h-[400px]">
                <div className="p-2 border-b bg-muted/20 flex items-center justify-between text-xs font-medium">
                  <span>分析結果</span>
                  {sseLoading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                </div>
                <div ref={sseResultRef} className="flex-1 overflow-y-auto p-4 prose prose-sm dark:prose-invert">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sseResult}</ReactMarkdown>
                </div>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            {!sseLoading && !sseResult && (
               <Button onClick={startTransientAnalysis} disabled={pdfFiles.length === 0} className="w-full">
                 開始分析文件
               </Button>
            )}
            {sseLoading && (
               <Button variant="destructive" onClick={abort} className="w-full">
                 停止分析
               </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
